'use client';

import { useContext, createContext, FC, PropsWithChildren } from 'react';
import { storage } from '@bts-web/utils-storage';
import {
  createQuestionContextProviderFactory,
  QuestionContextProps,
  QuestionAnswer,
} from '../QuestionContextProviderFactory.use-client';
import { getComplexProductsAppropriatenessQuestions } from '@bts-web/core-features/common';

const COMPLEX_PRODUCTS_APPROPRIATENESS_QUESTION_ANSWERS_STORAGE =
  'complex-products-appropriateness.answers';

export const ComplexProductsAppropriatenessQuestionContext =
  createContext<QuestionContextProps>(null);

export function useComplexProductsAppropriatenessQuestionContext() {
  const contextValue = useContext(
    ComplexProductsAppropriatenessQuestionContext,
  );

  if (contextValue === null) {
    throw new Error(
      'useComplexProductsAppropriatenessQuestionContext must be used within a ComplexProductsAppropriatenessQuestionContextProvider',
    );
  }

  return contextValue;
}

export const saveComplexProductsQuestionAnswers = (
  answers: QuestionAnswer[],
) => {
  storage.setItem(
    COMPLEX_PRODUCTS_APPROPRIATENESS_QUESTION_ANSWERS_STORAGE,
    answers,
  );
};

export const removeComplexProductsQuestionAnswers = () => {
  storage.removeItem(COMPLEX_PRODUCTS_APPROPRIATENESS_QUESTION_ANSWERS_STORAGE);
};

const ComplexProductsAppropriatenessQuestionProviderElement =
  createQuestionContextProviderFactory({
    Context: ComplexProductsAppropriatenessQuestionContext,
    id: COMPLEX_PRODUCTS_APPROPRIATENESS_QUESTION_ANSWERS_STORAGE,
    initialQuestionAnswers: () =>
      storage.getItem(
        COMPLEX_PRODUCTS_APPROPRIATENESS_QUESTION_ANSWERS_STORAGE,
      ) ?? [],
    saveQuestionAnswers: saveComplexProductsQuestionAnswers,
    removeQuestionAnswers: removeComplexProductsQuestionAnswers,
  });

export const ComplexProductsAppropriatenessQuestionContextProvider: FC<
  PropsWithChildren<{
    initialData: Awaited<
      ReturnType<typeof getComplexProductsAppropriatenessQuestions>
    >;
  }>
> = ({ initialData, children }) => {
  return (
    <ComplexProductsAppropriatenessQuestionProviderElement
      initialQuestions={
        initialData as Parameters<
          typeof ComplexProductsAppropriatenessQuestionProviderElement
        >[0]['initialQuestions']
      }
    >
      {children}
    </ComplexProductsAppropriatenessQuestionProviderElement>
  );
};
